/**
 * PlansTable — the shared Resource Plan summary table (RP-15.1, RP-15.2).
 *
 * Rendered on the Solution Detail drawer's Plans tab and the Account Profile's
 * Solutions & Plans card, so both surfaces agree on what a plan row looks like. This
 * component is pure presentation: callers own the `useResourcePlans` query and its
 * loading / error states, and pass only the resolved `plans` array in on success.
 *
 * Per RP-15.1: the Active plan pins to the top with an Active badge; Superseded plans
 * render muted underneath everything else. Every header is sortable; choosing a column
 * overrides that default order (see src/lib/plan-list-sort.ts).
 *
 * A row opens its plan on double-click, or through the Open arrow at its end. Single clicks
 * stay with the plan-name link and the sort headers, so nothing navigates by accident.
 */

import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowRight, ChevronDown, ChevronsUpDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import { ConcernCountBadge } from "@/components/plans/ConcernCountBadge";
import { StaffingNeedBadge } from "@/components/plans/StaffingNeedBadge";
import { Button } from "@/components/ui/button";
import { PLAN_LIST_COLUMNS, type PlanListColumn } from "@/lib/plan-list-columns";
import { nextPlanSort, type PlanSort, type PlanSortColumn, sortPlans } from "@/lib/plan-list-sort";
import { planStatusLabel, resourcingStatusLabel } from "@/lib/plan-status-labels";
import type { MarginStatus } from "@/lib/rate-card";
import { cn } from "@/lib/utils";
import type { PlanResourcingStatus, ResourcePlanSummaryView } from "@/schemas/resource-plans";

const PRICING_MODEL_LABEL: Record<string, string> = {
  TimeAndMaterials: "T&M",
  FixedPrice: "Fixed Price",
};

const PLAN_STATUS_CLASS: Record<string, string> = {
  Draft: "bg-muted text-muted-foreground",
  Proposed: "bg-info-100 text-info-700",
  SoftCommitted: "bg-warning-100 text-warning-700",
  Won: "bg-success-100 text-success-700",
  HandedOver: "bg-brand-100 text-brand-700",
  // Legacy (retired 2026-10-14) — still shown on frozen versions and history.
  Staffed: "bg-success-200 text-success-900",
  Active: "bg-success-100 text-success-700",
  Closed: "bg-muted text-muted-foreground",
  Lost: "bg-error-100 text-error-700",
  Superseded: "bg-muted text-muted-foreground",
  Locked: "bg-muted text-foreground",
};

/** The same status meaning as a dot, for the plan header's select-style pickers. */
const PLAN_STATUS_DOT: Record<string, string> = {
  Proposed: "bg-info-500",
  SoftCommitted: "bg-warning-500",
  Won: "bg-success-500",
  HandedOver: "bg-brand-500",
  Staffed: "bg-success-600",
  Active: "bg-success-500",
  Lost: "bg-error-500",
};

export function planStatusDotClass(status: string): string {
  return PLAN_STATUS_DOT[status] ?? "bg-muted-foreground/50";
}

/** Reused on the Solutions Dashboard's compact Plan Status column too. */
export function PlanStatusBadge({ status }: { status: string }) {
  const cls = PLAN_STATUS_CLASS[status] ?? "bg-muted text-muted-foreground";
  const label = planStatusLabel(status);
  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-sm font-medium",
        cls,
      )}
    >
      {label}
    </span>
  );
}

const RESOURCING_STATUS_CLASS: Record<PlanResourcingStatus, string> = {
  InReview: "bg-info-100 text-info-700",
  Staffing: "bg-warning-100 text-warning-700",
  Staffed: "bg-success-100 text-success-700",
  Returned: "bg-error-100 text-error-700",
};

const RESOURCING_STATUS_DOT: Record<PlanResourcingStatus, string> = {
  InReview: "bg-info-500",
  Staffing: "bg-warning-500",
  Staffed: "bg-success-500",
  Returned: "bg-error-500",
};

export function resourcingStatusDotClass(status: PlanResourcingStatus | null | undefined): string {
  return status ? RESOURCING_STATUS_DOT[status] : "bg-muted-foreground/50";
}

/** The Resource Manager's staffing status — separate from `PlanStatusBadge`'s sales
 * status. Muted em-dash when nothing has been set yet. */
export function ResourcingStatusBadge({
  status,
}: {
  status: PlanResourcingStatus | null | undefined;
}) {
  if (!status) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }
  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-sm font-medium",
        RESOURCING_STATUS_CLASS[status],
      )}
    >
      {resourcingStatusLabel(status)}
    </span>
  );
}

const MARGIN_STATUS_CLASS: Record<MarginStatus, string> = {
  green: "bg-success-100 text-success-700",
  amber: "bg-warning-100 text-warning-700",
  red: "bg-error-100 text-error-700",
};

function isMarginStatus(value: string): value is MarginStatus {
  return value === "green" || value === "amber" || value === "red";
}

/** RP-D3: red is below the floor (needs approval); amber is a warning band ABOVE the
 * floor, not a failure below it. */
function MarginBadge({ percent, status }: { percent: string | null; status: string | null }) {
  if (percent === null || status === null) {
    return <span className="text-sm tabular-nums text-muted-foreground">—</span>;
  }
  const cls = isMarginStatus(status)
    ? MARGIN_STATUS_CLASS[status]
    : "bg-muted text-muted-foreground";
  const title =
    status === "red"
      ? "Below the margin floor — this plan needs approval."
      : status === "amber"
        ? "Above the margin floor, inside the warning band — a caution, not a failure."
        : "Comfortably above the margin floor.";
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-sm font-semibold tabular-nums cursor-help",
        cls,
      )}
      title={title}
    >
      {percent}%
    </span>
  );
}

/** Insert thousand separators into a decimal string without parsing it as a float —
 * money and hours cross this boundary as strings for a reason (see schemas/resource-plans.ts). */
function formatThousands(value: string): string {
  const [whole, frac] = value.split(".");
  const withCommas = (whole ?? value).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return frac !== undefined ? `${withCommas}.${frac}` : withCommas;
}

function formatMoney(value: string): string {
  return `$${formatThousands(value)}`;
}

function formatHours(value: string): string {
  return `${formatThousands(value)} hrs`;
}

function formatMonth(value: string | null): string {
  if (!value) return "—";
  const [year, month] = value.split("-");
  if (!year || !month) return value;
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

function formatUpdatedAt(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

interface PlansTableProps {
  plans: ResourcePlanSummaryView[];
  emptyMessage: string;
  showSolution?: boolean;
  /**
   * Off on surfaces already scoped to one Account (the Account Profile, a Solution). With
   * `showSolution` too, the Account and Solution stack in one column.
   */
  showClient?: boolean;
  /**
   * Initial sort, before any header click. Omit to keep RP-15.1's default order (Active
   * pinned, Superseded sunk, then newest first) — only the all-plans list overrides it.
   */
  defaultSort?: PlanSort;
  /**
   * Optional columns to show, from the list page's Columns menu. Omit to show them all
   * (the Solution drawer and Account Profile). The identity columns always show.
   */
  columns?: readonly PlanListColumn[];
}

/** A double-click on a link, button or control belongs to that control, not the row. */
function fromInteractiveElement(target: EventTarget): boolean {
  return (
    target instanceof Element &&
    target.closest("a, button, input, select, textarea, [role='button']") !== null
  );
}

const TH_CLASS = "px-3 py-2 font-semibold whitespace-nowrap";
const TD_CLASS = "px-3 py-2 whitespace-nowrap";

function SortableHeader({
  column,
  label,
  align = "left",
  sort,
  onSort,
  title,
}: {
  column: PlanSortColumn;
  label: string;
  align?: "left" | "right";
  sort: PlanSort | null;
  onSort: (column: PlanSortColumn) => void;
  title?: string;
}) {
  const active = sort?.column === column;
  return (
    <th
      className={cn(TH_CLASS, align === "right" && "text-right")}
      aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
      title={title}
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className={cn(
          "inline-flex items-center gap-1 uppercase tracking-wider hover:text-foreground",
          align === "right" && "flex-row-reverse",
          active && "text-foreground",
        )}
      >
        <span>{label}</span>
        {active ? (
          sort.direction === "asc" ? (
            <ChevronUp className="size-3.5" />
          ) : (
            <ChevronDown className="size-3.5" />
          )
        ) : (
          <ChevronsUpDown className="size-3.5 opacity-30" />
        )}
      </button>
    </th>
  );
}

export function PlansTable({
  plans,
  emptyMessage,
  showSolution = false,
  showClient = false,
  defaultSort,
  columns,
}: PlansTableProps) {
  const navigate = useNavigate();
  const [sort, setSort] = useState<PlanSort | null>(defaultSort ?? null);
  const sorted = sortPlans(plans, sort);
  const show = (column: PlanListColumn) => columns === undefined || columns.includes(column);
  const optionalCount = columns?.length ?? PLAN_LIST_COLUMNS.length;
  // Name + the optional columns + the Open arrow, plus Account / Solution when shown.
  const columnCount = 2 + optionalCount + (showSolution || showClient ? 1 : 0);
  const onSort = (column: PlanSortColumn) => setSort((prev) => nextPlanSort(prev, column));
  const header = { sort, onSort };

  return (
    <div className="overflow-x-auto rounded-md border border-border bg-card">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            {showClient ? (
              <SortableHeader
                column="client"
                label={showSolution ? "Account / Solution" : "Account"}
                {...header}
              />
            ) : (
              showSolution && <SortableHeader column="solution" label="CRM solution" {...header} />
            )}
            <SortableHeader column="name" label="Name" {...header} />
            {show("author") && (
              <SortableHeader
                column="author"
                label="Author"
                title="Plan Owner — who created this plan"
                {...header}
              />
            )}
            {show("status") && <SortableHeader column="status" label="Status" {...header} />}
            {show("resourcingStatus") && (
              <SortableHeader
                column="resourcingStatus"
                label="Resourcing"
                title="The Resource Manager's staffing status — separate from the plan's sales status."
                {...header}
              />
            )}
            {show("staffingNeed") && (
              <SortableHeader
                column="staffingNeed"
                label="Staffing need"
                title="How urgent the Solution's open Staffing Requests are — the most urgent priority. Set on the request."
                {...header}
              />
            )}
            {show("pricingModel") && (
              <SortableHeader column="pricingModel" label="Pricing Model" {...header} />
            )}
            {show("start") && <SortableHeader column="start" label="Start" {...header} />}
            {show("end") && <SortableHeader column="end" label="End" {...header} />}
            {show("totalHours") && (
              <SortableHeader column="totalHours" label="Total Hours" align="right" {...header} />
            )}
            {show("totalRevenue") && (
              <SortableHeader
                column="totalRevenue"
                label="Total Revenue"
                align="right"
                {...header}
              />
            )}
            {show("margin") && <SortableHeader column="margin" label="Margin" {...header} />}
            {show("updatedAt") && (
              <SortableHeader column="updatedAt" label="Last Updated" {...header} />
            )}
            <th className={TH_CLASS}>
              <span className="sr-only">Open</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((plan) => {
            const superseded = plan.status === "Superseded";
            return (
              <tr
                key={plan.id}
                title="Double-click to open this plan"
                onDoubleClick={(e) => {
                  if (fromInteractiveElement(e.target)) return;
                  navigate({ to: "/dashboard/plans/$planId", params: { planId: plan.id } });
                }}
                className={cn(
                  "border-t border-border transition-colors hover:bg-muted/40",
                  superseded && "opacity-60",
                  plan.status === "Active" && "bg-success-50/40",
                )}
              >
                {(showClient || showSolution) && (
                  <td className={TD_CLASS}>
                    {showClient && <div className="text-foreground">{plan.clientAccountName}</div>}
                    {showSolution && (
                      <div className={cn("text-muted-foreground", showClient && "text-xs")}>
                        {plan.solutionName}
                      </div>
                    )}
                  </td>
                )}
                <td className={cn(TD_CLASS, "font-medium text-foreground")}>
                  <Link
                    to="/dashboard/plans/$planId"
                    params={{ planId: plan.id }}
                    className="hover:underline"
                  >
                    {plan.name}
                  </Link>
                  <ConcernCountBadge summary={plan.concerns} />
                </td>
                {show("author") && (
                  <td className={cn(TD_CLASS, "text-muted-foreground")}>
                    {plan.ownerName ?? "Unassigned"}
                  </td>
                )}
                {show("status") && (
                  <td className={TD_CLASS}>
                    <PlanStatusBadge status={plan.status} />
                  </td>
                )}
                {show("resourcingStatus") && (
                  <td className={TD_CLASS}>
                    <ResourcingStatusBadge status={plan.resourcingStatus} />
                  </td>
                )}
                {show("staffingNeed") && (
                  <td className={TD_CLASS}>
                    <StaffingNeedBadge need={plan.staffingNeed} />
                  </td>
                )}
                {show("pricingModel") && (
                  <td className={TD_CLASS}>
                    {PRICING_MODEL_LABEL[plan.pricingModel] ?? plan.pricingModel}
                  </td>
                )}
                {show("start") && <td className={TD_CLASS}>{formatMonth(plan.startMonth)}</td>}
                {show("end") && <td className={TD_CLASS}>{formatMonth(plan.endMonth)}</td>}
                {show("totalHours") && (
                  <td className={cn(TD_CLASS, "text-right tabular-nums")}>
                    {formatHours(plan.totalHours)}
                  </td>
                )}
                {show("totalRevenue") && (
                  <td className={cn(TD_CLASS, "text-right tabular-nums")}>
                    {formatMoney(plan.totalRevenue)}
                  </td>
                )}
                {show("margin") && (
                  <td className={TD_CLASS}>
                    <MarginBadge percent={plan.marginOfRecordPercent} status={plan.marginStatus} />
                  </td>
                )}
                {show("updatedAt") && (
                  <td className={cn(TD_CLASS, "text-muted-foreground")}>
                    {formatUpdatedAt(plan.updatedAt)}
                  </td>
                )}
                <td className={cn(TD_CLASS, "w-px text-right")}>
                  <Button asChild variant="ghost" size="icon-xs">
                    <Link
                      to="/dashboard/plans/$planId"
                      params={{ planId: plan.id }}
                      aria-label={`Open ${plan.name}`}
                      title="Open plan"
                    >
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </td>
              </tr>
            );
          })}
          {sorted.length === 0 && (
            <tr>
              <td
                colSpan={columnCount}
                className="px-6 py-12 text-center text-sm text-muted-foreground"
              >
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
